import { useState, useEffect } from 'react';
import { useTerminal } from '@/layouts/LocalStoreLayout';
import { localCashRegisterAPI, localPosAPI } from '@/services/localApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  DollarSign, Lock, Unlock, Clock, TrendingUp,
  Banknote, CreditCard, Smartphone, AlertCircle, CheckCircle
} from 'lucide-react';

const LocalCashRegister = () => {
  const { terminalId, terminal, storeInfo } = useTerminal();

  const [register, setRegister] = useState(null);
  const [loading, setLoading] = useState(true);
  const [todaySummary, setTodaySummary] = useState(null);
  const [history, setHistory] = useState([]);

  // Modal states
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [openingAmount, setOpeningAmount] = useState('');
  const [closingAmount, setClosingAmount] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, [terminalId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [registerRes, summaryRes, historyRes] = await Promise.all([
        localCashRegisterAPI.getCurrent(terminalId),
        localPosAPI.getTodaySummary(terminalId),
        localCashRegisterAPI.getHistory(terminalId, { limit: 10 })
      ]);

      setRegister(registerRes.data.register);
      setTodaySummary(summaryRes.data);
      setHistory(historyRes.data.registers || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load register data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRegister = async () => {
    if (!openingAmount || parseFloat(openingAmount) < 0) {
      toast.error('Please enter a valid opening amount');
      return;
    }

    try {
      setProcessing(true);
      await localCashRegisterAPI.open({
        terminal_id: terminalId,
        opening_amount: parseFloat(openingAmount),
        opened_by: 'pos_user'
      });

      toast.success('Cash register opened');
      setShowOpenModal(false);
      setOpeningAmount('');
      loadData();
    } catch (error) {
      console.error('Failed to open register:', error);
      toast.error(error.response?.data?.detail || 'Failed to open register');
    } finally {
      setProcessing(false);
    }
  };

  const handleCloseRegister = async () => {
    if (!closingAmount || parseFloat(closingAmount) < 0) {
      toast.error('Please enter a valid closing amount');
      return;
    }

    try {
      setProcessing(true);
      const res = await localCashRegisterAPI.close(register.id, {
        closing_amount: parseFloat(closingAmount),
        closed_by: 'pos_user',
        notes: closingNotes
      });

      const summary = res.data.summary;
      const diff = summary.difference;

      if (Math.abs(diff) < 0.01) {
        toast.success('Register closed - Cash matches!');
      } else if (diff > 0) {
        toast.warning(`Register closed - Excess ₹${diff.toFixed(2)}`);
      } else {
        toast.warning(`Register closed - Short ₹${Math.abs(diff).toFixed(2)}`);
      }

      setShowCloseModal(false);
      setClosingAmount('');
      setClosingNotes('');
      loadData();
    } catch (error) {
      console.error('Failed to close register:', error);
      toast.error(error.response?.data?.detail || 'Failed to close register');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p>Loading...</p>
      </div>
    );
  }

  const expectedCash = register
    ? register.opening_amount + register.cash_sales
    : 0;

  return (
    <div className="p-6 space-y-6" data-testid="local-cash-register-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cash Register</h1>
          <p className="text-gray-500">Terminal: {terminalId}</p>
        </div>
        
        {register ? (
          <Button
            variant="destructive"
            onClick={() => setShowCloseModal(true)}
            data-testid="close-register-btn"
          >
            <Lock className="w-4 h-4 mr-2" />
            Close Register
          </Button>
        ) : (
          <Button
            className="bg-green-600 hover:bg-green-700"
            onClick={() => setShowOpenModal(true)}
            data-testid="open-register-btn"
          >
            <Unlock className="w-4 h-4 mr-2" />
            Open Register
          </Button>
        )}
      </div>

      {/* Current Register Status */}
      {register ? (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                <CardTitle className="text-green-800">Register Open</CardTitle>
              </div>
              <Badge variant="secondary" className="bg-green-200 text-green-800">
                Since {formatTime(register.opened_at)}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-white rounded-lg p-4 border">
                <p className="text-sm text-gray-500">Opening</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatCurrency(register.opening_amount)}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <div className="flex items-center gap-2 mb-1">
                  <Banknote className="w-4 h-4 text-green-600" />
                  <p className="text-sm text-gray-500">Cash Sales</p>
                </div>
                <p className="text-xl font-bold text-green-600">
                  {formatCurrency(register.cash_sales)}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className="w-4 h-4 text-blue-600" />
                  <p className="text-sm text-gray-500">Card Sales</p>
                </div>
                <p className="text-xl font-bold text-blue-600">
                  {formatCurrency(register.card_sales)}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border">
                <div className="flex items-center gap-2 mb-1">
                  <Smartphone className="w-4 h-4 text-purple-600" />
                  <p className="text-sm text-gray-500">UPI Sales</p>
                </div>
                <p className="text-xl font-bold text-purple-600">
                  {formatCurrency(register.upi_sales)}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-green-300">
                <p className="text-sm text-gray-500">Expected Cash</p>
                <p className="text-xl font-bold text-green-700">
                  {formatCurrency(expectedCash)}
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between bg-white rounded-lg p-4 border">
              <div>
                <p className="text-sm text-gray-500">Total Sales</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(register.total_sales)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Transactions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {register.total_transactions}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-yellow-200 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-yellow-700" />
              </div>
              <div>
                <p className="font-semibold text-yellow-800">No Open Register</p>
                <p className="text-yellow-700 text-sm">
                  Open a cash register to start processing sales
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Today's Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Total Bills</p>
              <p className="text-2xl font-bold">{todaySummary?.total_bills || 0}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Total Sales</p>
              <p className="text-2xl font-bold">{formatCurrency(todaySummary?.total_sales)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Cash</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(todaySummary?.cash_sales)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Card + UPI</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency((todaySummary?.card_sales || 0) + (todaySummary?.upi_sales || 0))}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Register History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Recent Shifts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No register history</p>
          ) : (
            <div className="space-y-3">
              {history.filter(h => h.status === 'closed').slice(0, 5).map((reg) => (
                <div
                  key={reg.id}
                  className="flex items-center justify-between bg-gray-50 rounded-lg p-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {new Date(reg.opened_at).toLocaleDateString('en-IN')}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatTime(reg.opened_at)} - {formatTime(reg.closed_at)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{formatCurrency(reg.total_sales)}</p>
                    <p className="text-sm text-gray-500">{reg.total_transactions} bills</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Open Register Modal */}
      <Dialog open={showOpenModal} onOpenChange={setShowOpenModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Open Cash Register</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Opening Cash Amount</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                <Input
                  type="number"
                  value={openingAmount}
                  onChange={(e) => setOpeningAmount(e.target.value)}
                  placeholder="0.00"
                  className="pl-8 text-lg"
                  data-testid="opening-amount-input"
                />
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Enter the cash you're starting with
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOpenModal(false)}>
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={handleOpenRegister}
              disabled={processing}
              data-testid="confirm-open-btn"
            >
              {processing ? 'Opening...' : 'Open Register'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Register Modal */}
      <Dialog open={showCloseModal} onOpenChange={setShowCloseModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Close Cash Register</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Opening Amount</span>
                <span className="font-medium">{formatCurrency(register?.opening_amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Cash Sales</span>
                <span className="font-medium text-green-600">+{formatCurrency(register?.cash_sales)}</span>
              </div>
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="font-semibold">Expected Cash</span>
                <span className="font-bold text-lg">{formatCurrency(expectedCash)}</span>
              </div>
            </div>

            {/* Closing Amount */}
            <div>
              <Label>Actual Cash in Drawer</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                <Input
                  type="number"
                  value={closingAmount}
                  onChange={(e) => setClosingAmount(e.target.value)}
                  placeholder="0.00"
                  className="pl-8 text-lg"
                  data-testid="closing-amount-input"
                />
              </div>
            </div>

            {/* Difference */}
            {closingAmount && (
              <div className={`rounded-lg p-3 ${
                parseFloat(closingAmount) === expectedCash
                  ? 'bg-green-100 text-green-800'
                  : parseFloat(closingAmount) > expectedCash
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
              }`}>
                <div className="flex justify-between items-center">
                  <span>Difference</span>
                  <span className="font-bold">
                    {parseFloat(closingAmount) >= expectedCash ? '+' : ''}
                    {formatCurrency(parseFloat(closingAmount) - expectedCash)}
                  </span>
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={closingNotes}
                onChange={(e) => setClosingNotes(e.target.value)}
                placeholder="Any remarks..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseModal(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleCloseRegister}
              disabled={processing}
              data-testid="confirm-close-btn"
            >
              {processing ? 'Closing...' : 'Close Register'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocalCashRegister;
